// @ts-nocheck
// Admin purchases tab: list, manual grant, revoke. Backed by the three
// admin_* RPCs introduced in migration 0014. When buyers email "paid
// but no access", this is the page that resolves it without a SQL
// session.

import { useCallback, useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { useTableSort, SortableTH } from "./useTableSort";

type Row = {
  purchase_id: string;
  user_id: string;
  email: string | null;
  display_name: string | null;
  amount_cents: number | null;
  currency: string | null;
  provider: string;
  provider_tx_id: string;
  status: string;
  license_id: string | null;
  license_status: string | null;
  license_sku: string | null;
  created_at: string;
};

function formatAmount(cents: number | null, currency: string | null): string {
  if (cents == null) return "—";
  const value = (cents / 100).toLocaleString("he-IL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const symbol = currency === "ILS" ? "₪" : currency || "";
  return `${value} ${symbol}`.trim();
}

function statusPillClass(status: string | null): string {
  if (status === "active") return "adm-pill ok";
  if (status === "refunded" || status === "revoked") return "adm-pill warn";
  if (status === "pending") return "adm-pill dim";
  return "adm-pill dim";
}

// Hebrew label for the status pill. A purchase with no license of its own
// ("completed") is a gift: the access went to the recipient's invitation.
function statusLabel(status: string): string {
  if (status === "active") return "רכישה";
  if (status === "completed") return "מתנה";
  if (status === "revoked") return "בוטל";
  if (status === "refunded") return "הוחזר";
  if (status === "pending") return "ממתין";
  return status;
}

export function AdminPurchases() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");

  // Grant form
  const [grantEmail, setGrantEmail] = useState("");
  const [grantNote, setGrantNote] = useState("");
  const [grantBusy, setGrantBusy] = useState(false);
  const [grantMsg, setGrantMsg] = useState<{ tone: "ok" | "err"; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const { data, error: e } = await supabase.rpc("admin_list_purchases", {
      _query: query.trim() || null,
    });
    if (e) {
      setError(e.message);
      setLoading(false);
      return;
    }
    setRows((data || []) as Row[]);
    setLoading(false);
  }, [query]);

  useEffect(() => {
    load();
  }, [load]);

  const handleGrant = async (e: React.FormEvent) => {
    e.preventDefault();
    setGrantMsg(null);
    setGrantBusy(true);
    const { error: rpcErr } = await supabase.rpc("admin_grant_license", {
      _email: grantEmail.trim(),
      _note: grantNote.trim() || null,
    });
    setGrantBusy(false);
    if (rpcErr) {
      setGrantMsg({ tone: "err", text: rpcErr.message });
      return;
    }
    setGrantMsg({
      tone: "ok",
      text: `נפתחה גישה ל‑${grantEmail.trim()}`,
    });
    setGrantEmail("");
    setGrantNote("");
    load();
  };

  const handleRevoke = async (row: Row) => {
    if (!row.license_id) return;
    const reason = window.prompt(
      `סיבת הביטול עבור ${row.email ?? "המשתמש"} (יישמר באוּדיט):`,
      ""
    );
    if (reason === null) return;
    const { error: rpcErr } = await supabase.rpc("admin_revoke_license", {
      _license_id: row.license_id,
      _reason: reason || null,
    });
    if (rpcErr) {
      window.alert("ביטול נכשל: " + rpcErr.message);
      return;
    }
    load();
  };

  const { sortBy, sortIndicator, sortedRows } = useTableSort(rows, {
    created_at:    (r) => r.created_at,
    email:         (r) => r.email,
    sku:           (r) => r.license_sku,
    amount:        (r) => r.amount_cents,
    provider:      (r) => r.provider,
    provider_tx_id: (r) => r.provider_tx_id,
    status:        (r) => r.license_status ?? r.status,
  });

  return (
    <>
      <div className="adm-card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginBottom: 12, fontFamily: "'Frank Ruhl Libre', serif" }}>
          פתיחת גישה ידנית
        </h3>
        <p style={{ fontSize: 13, color: "#5C3D2E", marginBottom: 14, lineHeight: 1.6 }}>
          כשמישהו שילם דרך העברה / טעות בכרטיס / בעיה טכנית: בקשו ממנו להירשם
          לאתר (Magic Link), ואז הקלידו פה את המייל שלו ולחצו פתיחה.
        </p>
        <form onSubmit={handleGrant} className="adm-grant-form">
          <input
            type="email"
            required
            placeholder="מייל הרוכש"
            value={grantEmail}
            onChange={(e) => setGrantEmail(e.target.value)}
            className="adm-search"
            style={{ flex: 1, minWidth: 200 }}
          />
          <input
            type="text"
            placeholder="הערה (העברה בנקאית, החזר ידני וכו')"
            value={grantNote}
            onChange={(e) => setGrantNote(e.target.value)}
            className="adm-search"
            style={{ flex: 2, minWidth: 200 }}
          />
          <button className="adm-btn" type="submit" disabled={grantBusy}>
            {grantBusy ? "פותח…" : "פתיחת גישה"}
          </button>
        </form>
        {grantMsg && (
          <div
            className={"adm-" + (grantMsg.tone === "ok" ? "ok" : "error")}
            style={{ marginTop: 10 }}
          >
            {grantMsg.text}
          </div>
        )}
      </div>

      <div className="adm-toolbar">
        <input
          className="adm-search"
          placeholder="חיפוש לפי מייל או מספר עסקה…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <button className="adm-btn ghost" onClick={load} disabled={loading}>
          רענון
        </button>
      </div>

      {error && <div className="adm-error">{error}</div>}

      <div className="adm-card" style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div className="adm-loading">טוען רכישות…</div>
        ) : rows.length === 0 ? (
          <div className="adm-empty">אין רכישות תואמות.</div>
        ) : (
          <table className="adm-table">
            <thead>
              <tr>
                <th>#</th>
                <SortableTH k="created_at" sortBy={sortBy} sortIndicator={sortIndicator}>תאריך</SortableTH>
                <SortableTH k="email" sortBy={sortBy} sortIndicator={sortIndicator}>מייל</SortableTH>
                <SortableTH k="sku" sortBy={sortBy} sortIndicator={sortIndicator}>סוג</SortableTH>
                <SortableTH k="amount" sortBy={sortBy} sortIndicator={sortIndicator}>סכום</SortableTH>
                <SortableTH k="provider" sortBy={sortBy} sortIndicator={sortIndicator}>ספק</SortableTH>
                <SortableTH k="provider_tx_id" sortBy={sortBy} sortIndicator={sortIndicator}>מספר עסקה</SortableTH>
                <SortableTH k="status" sortBy={sortBy} sortIndicator={sortIndicator}>סטטוס</SortableTH>
                <th>פעולה</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((r, i) => (
                <tr key={r.purchase_id}>
                  <td className="num" style={{ color: "#8B6F5E" }}>{i + 1}</td>
                  <td className="num">
                    {new Date(r.created_at).toLocaleDateString("he-IL")}
                  </td>
                  <td className="mono">{r.email ?? "—"}</td>
                  <td>{r.license_sku ?? "—"}</td>
                  <td className="num">{formatAmount(r.amount_cents, r.currency)}</td>
                  <td>{r.provider}</td>
                  <td className="mono" style={{ fontSize: 11 }}>
                    {r.provider_tx_id.length > 24
                      ? r.provider_tx_id.slice(0, 8) + "…" + r.provider_tx_id.slice(-6)
                      : r.provider_tx_id}
                  </td>
                  <td>
                    <span className={statusPillClass(r.license_status)}>
                      {statusLabel(r.license_status ?? r.status)}
                    </span>
                  </td>
                  <td>
                    {r.license_id && r.license_status === "active" ? (
                      <button
                        className="adm-btn ghost"
                        onClick={() => handleRevoke(r)}
                        style={{ fontSize: 12, padding: "4px 10px" }}
                      >
                        ביטול
                      </button>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
